import { createUserError } from "../utils/errorHandler";
import i18n from "../i18n";

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY;

// Configuration des headers pour les requêtes API
const getHeaders = (includeAuth = false, accessToken = null) => {

  const currentLanguage = i18n.language || localStorage.getItem("lang") || "fr";

  const headers = {
    'accept': 'application/json',
    'X-API-KEY': API_KEY,
    'X-Client-Type': 'onecc',
    'Content-Type': 'application/json',
    'Accept-Language': currentLanguage,
  };

  if (includeAuth && accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  return headers;
};

// Fonction pour extraire le message d'erreur
const extractErrorMessage = (data) => {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return null;

  // Priorité aux messages directs
  if (data.message) return data.message;
  if (data.error && typeof data.error === 'string') return data.error;
  if (data.detail) return data.detail;

  // Recherche dans les objets imbriqués (ex: paymentMethod.details ou paymentMethod.error)
  for (const key in data) {
    if (typeof data[key] === 'object') {
      const nestedMessage = extractErrorMessage(data[key]);
      if (nestedMessage) return nestedMessage;
    }
  }

  return null;
};

// Fonction pour gérer les erreurs de réponse API
const handleApiError = async (response) => {
  let errorData;
  try {
    errorData = await response.json();
  } catch (e) {
    const error = new Error(`Invalid server response. (code de l'erreur: ${response.status})`);
    error.status = response.status;
    throw error;
  }

  const apiErrorMessage = extractErrorMessage(errorData);

  const error = new Error(apiErrorMessage || `Erreur ${response.status}`);
  error.status = response.status;
  error.data = errorData;
  error.isApiError = true;
  throw error;
};

// Récupérer les informations de la borne
export const getChargingStationInfo = async (evcpReference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}?article=CHARGE_NOW`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération des informations de la borne:', error);
    throw error;
  }
};

// Vérifier l'éligibilité au One-Click Charge
export const checkOneClickChargeEligibility = async (evcpReference) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}/one-click-charge-eligibility`, {
      method: 'GET',
      headers: getHeaders(),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'éligibilité:', error);
    throw error;
  }
};

// Configurer le One-Click Charge avec Stripe
export const setupOneClickCharge = async (email) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/setup`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        email,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la configuration du Scan to Charge:', error);
    throw error;
  }
};

// Reprendre une session via le token du lien email (Canal A — re-scan/lien email)
export const redeemSessionToken = async (sessionToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/sessions/${sessionToken}/token`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la reprise de session via le lien email:', error);
    throw error;
  }
};

// Reprendre une session active via les 4 derniers chiffres de la CB (re-scan QR)
export const resumeSessionByCard = async (connectorId, last4digits) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/sessions/resume`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        connectorId,
        last4digits,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la reprise de session:', error);
    throw error;
  }
};

// Pré-autorisation CB
export const createAuthorization = async (chargeNowType, connectorId, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/stripe/authorizations`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({
        chargeNowType,
        connectorId
      })
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la pré-autorisation:', error);
    throw error;
  }
};

export const attachePaymentMethod = async (paymentMethodId, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/stripe/payment-methods`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({
        paymentMethodId,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error("Erreur lors de l'attachement de la methode de paiement:", error);
    throw error;
  }
};

// Démarrer la charge
export const startCharging = async (evcpReference, connectorReference, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}/connectors/${connectorReference}/start-charge`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error('Erreur lors du démarrage de la charge:', error);
    throw error;
  }
};

// Suivre la transaction en cours
export const getOngoingTransaction = async (accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/me?groups=ongoing-use`, {
      method: 'GET',
      headers: getHeaders(true, accessToken),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors du suivi de la transaction:', error);
    throw error;
  }
};

// Envoyer la facture
export const sendInvoice = async (paymentIntentId, invoiceData, accessToken, transactionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/onecc/invoice-delivery/`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({
        paymentIntentId,
        transactionId,
        ...invoiceData,
      }),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de l\'envoi de la facture:', error);
    throw error;
  }
};

// Arreter la charge
export const stopCharging = async (evcpReference, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}/stop-charge`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de l\'arrêt de la charge:', error);
    throw error;
  }
};

// unlock 
export const unlockConnector = async (evcpReference, connectorNumber, accessToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/evcps/${evcpReference}/connectors/${connectorNumber}/unlock`, {
      method: 'POST',
      headers: getHeaders(true, accessToken),
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      await handleApiError(response);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors du déverrouillage du connecteur:', error);
    throw error;
  }
};